import { useState } from "react";

import { trpc } from "../trpc";

function authorLabel(m: {
  authorName: string | null;
  authorEmail: string | null;
}): string {
  return m.authorName ?? m.authorEmail ?? "Someone";
}

/** A threaded discussion attached to an annotation, term, or doha. */
export function Discussion({
  subjectType,
  subjectId,
}: {
  subjectType: "annotation" | "term" | "doha";
  subjectId: number;
}) {
  const utils = trpc.useUtils();
  const messages = trpc.threads.bySubject.useQuery({ subjectType, subjectId });
  const [body, setBody] = useState("");
  const post = trpc.threads.post.useMutation({
    onSuccess: async () => {
      setBody("");
      await utils.threads.bySubject.invalidate({ subjectType, subjectId });
    },
  });

  return (
    <div>
      <h3 className="eyebrow">Discussion</h3>
      <ul className="mt-3 space-y-4">
        {messages.data?.length ? (
          messages.data.map((m) => (
            <li
              key={m.id}
              className="rounded-xl border border-line bg-paper-raised px-4 py-3"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-ink">
                  {authorLabel(m)}
                </span>
                {m.authorRole === "helper" || m.authorRole === "admin" ? (
                  <span className="rounded-full bg-terracotta-wash px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-terracotta">
                    {m.authorRole === "admin" ? "admin" : "translator"}
                  </span>
                ) : null}
              </div>
              <p className="mt-1 font-serif text-lg leading-relaxed text-ink">
                {m.body}
              </p>
            </li>
          ))
        ) : (
          <li className="text-sm text-ink-faint">No replies yet.</li>
        )}
      </ul>

      <form
        className="mt-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (body.trim())
            post.mutate({ subjectType, subjectId, body: body.trim() });
        }}
      >
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder="Add to the discussion…"
          className="w-full rounded-xl border border-line-strong bg-paper-raised px-4 py-3 text-ink outline-none focus:border-terracotta/60"
        />
        <div className="mt-2 flex items-center justify-between">
          {post.error ? (
            <span className="text-sm text-terracotta">Sign in to reply.</span>
          ) : (
            <span />
          )}
          <button
            type="submit"
            disabled={!body.trim() || post.isPending}
            className="rounded-full bg-ink px-5 py-2 text-sm font-medium text-paper transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {post.isPending ? "Posting…" : "Post"}
          </button>
        </div>
      </form>
    </div>
  );
}
